import { set } from "lodash";
import moment from "moment";
import { createSelector } from "reselect";
import { makeGetTeamUsersSortedByName } from "~/store/selectors/selector.teams";
import { descSortCompare, sortCompare } from "~/utils/utils.jsx";
import {
  getPersonalizedConfigOption,
  getFirstWeekDay,
  getIsMultipleAssigneesEnabled,
} from "./selector.configOptions.jsx";
import { getActiveUnsortedProjectIdsWithoutSampleProjects, getAllProjects } from "./selector.projects.jsx";
import { getSampleProjectIds } from "./selector.projects.jsx";
import { getOpenSubtasks, getSubtasksArray } from "./selector.subtasks.jsx";
import { getAllTaskLists } from "./selector.taskLists.jsx";
import { getAllTasks, getOpenTasks } from "./selector.tasks.jsx";
import { getAllUsers, getLoggedUserId } from "./selector.users.jsx";

const RESULT_KEYS = ["overdue", "started", "today", "tomorrow", "restOfWeek", "nextWeek", "upcoming", "notSet"];

export const userWorkGroupByDate = createSelector(
  getAllTasks,
  getSubtasksArray,
  getLoggedUserId,
  getFirstWeekDay,
  getAllProjects,
  (state, groupBy) => groupBy,
  (state, groupBy, userIds) => userIds,
  getIsMultipleAssigneesEnabled,
  (tasks, subtasks, loggedUserId, firstDayOfWeek, projects, groupBy, userIds, multiAssignee) => {
    const result = {
      overdue: [],
      started: [],
      today: [],
      tomorrow: [],
      restOfWeek: [],
      nextWeek: [],
      upcoming: [],
      notSet: [],
    };

    const isForTeams = Array.isArray(userIds);
    const resolvedUserIds = !userIds ? [loggedUserId] : isForTeams ? userIds : [userIds];
    const userIdsSet = new Set(resolvedUserIds);
    const isStartDate = groupBy === "start_date";

    const today = moment.utc(moment().format("YYYY-MM-DD"));

    // Plain Set for O(1) project lookup — replaces Immutable .filter() + .get()
    const activeProjectIds = new Set();
    projects.forEach((project) => {
      if (!project.get("is_trashed") && !project.get("is_completed") && !project.get("is_sample")) {
        activeProjectIds.add(project.get("id"));
      }
    });

    const classify = (startOn, dueOn) => {
      if ((!dueOn && !isStartDate) || (!startOn && isStartDate)) return "notSet";
      const dueOnObj = moment.unix(dueOn).utc();
      if (today.isAfter(dueOnObj)) return "overdue";
      if (isStartDate && moment.unix(startOn).utc().isBefore(today) && !dueOnObj.isBefore(today)) return "started";
      const referenceObj = isStartDate ? moment.unix(startOn).utc() : dueOnObj;
      if (today.isSame(referenceObj)) return "today";
      if (referenceObj.diff(today, "days") === 1) return "tomorrow";
      if (today.isSame(referenceObj, "week")) return "restOfWeek";
      if (referenceObj.isSame(today.clone().add(1, "week"), "week")) return "nextWeek";
      return "upcoming";
    };

    // Single pass — no separate mapByAssignee build, no activeTasks Immutable filter
    tasks.forEach((task) => {
      if (task.get("is_completed") || task.get("is_trashed")) return;
      if (!activeProjectIds.has(task.get("project_id"))) return;

      const startOn = task.get("start_on");
      const dueOn = task.get("due_on");
      const taskId = task.get("id");
      const bucket = classify(startOn, dueOn);

      if (multiAssignee) {
        const assigneeIds = task.get("assignee_ids");
        if (!assigneeIds || assigneeIds.size === 0) return;
        assigneeIds.forEach((assigneeId) => {
          if (!userIdsSet.has(assigneeId)) return;
          const groupItem = {
            id: taskId,
            name: task.get("name"),
            dueOn: dueOn || null,
            startOn: startOn || null,
            isSubtask: false,
            taskId,
          };
          if (isForTeams) groupItem.assignee_id = assigneeId;
          result[bucket].push(groupItem);
        });
      } else {
        if (!userIdsSet.has(task.get("assignee_id"))) return;
        const groupItem = {
          id: taskId,
          name: task.get("name"),
          dueOn: dueOn || null,
          startOn: startOn || null,
          isSubtask: false,
          taskId,
        };
        if (isForTeams) groupItem.assignee_id = task.get("assignee_id");
        result[bucket].push(groupItem);
      }
    });

    subtasks.forEach((subtask) => {
      if (!subtask || subtask.is_trashed || subtask.is_completed) return;
      if (!userIdsSet.has(subtask.assignee_id)) return;
      const task = tasks.get(`${subtask.task_id}`);
      if (!task || task.get("is_completed") || task.get("is_trashed")) return;
      if (!activeProjectIds.has(task.get("project_id"))) return;

      // Preserve original: both fall back to subtask.due_on first, then task fields
      const dueOn = subtask.due_on || task.get("due_on") || null;
      const startOn = subtask.due_on || task.get("start_on") || null;

      const groupItem = {
        id: subtask.id,
        name: subtask.name,
        dueOn: dueOn || null,
        startOn: startOn || null,
        isSubtask: true,
        taskId: subtask.task_id,
      };
      if (isForTeams) groupItem.assignee_id = subtask.assignee_id;
      result[classify(startOn, dueOn)].push(groupItem);
    });

    const sortByKey = isStartDate ? "startOn" : "dueOn";
    for (const key of RESULT_KEYS) {
      result[key].sort((a, b) => {
        if (a[sortByKey] === b[sortByKey]) {
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        }
        return a[sortByKey] < b[sortByKey] ? -1 : 1;
      });
    }

    return result;
  }
);

export const getTasksGroupedByDateForVirtualization = createSelector(
  (state, groupBy, userIdsArray) => userWorkGroupByDate(state, groupBy, userIdsArray),
  (initialData = {}) => {
    const groupsForVirtualizedList = Object.keys(initialData)
      .filter((group) => Object.values(initialData[group]).length)
      .map((group, index) => {
        const items = Object.values(initialData[group]).map((task) => {
          return { ...task, type: task.isSubtask ? "subtask" : "task", collection: group };
        });

        const dateGroup = {
          id: index,
          name: group,
        };

        return {
          ...dateGroup,
          items,
        };
      });

    const rows = [];
    groupsForVirtualizedList.forEach((group) => {
      rows.push({
        type: "date",
        name: group.name,
      });
      group.items.map((item) => {
        rows.push({
          type: item.isSubtask ? "subtask" : "task",
          object: item,
        });
      });
    });

    return rows;
  }
);

export const makeGetTeamWorkGroupByAssignee = (teamId) => {
  return createSelector(
    getOpenTasks,
    getOpenSubtasks,
    getActiveUnsortedProjectIdsWithoutSampleProjects,
    makeGetTeamUsersSortedByName(teamId),
    getAllUsers,
    getIsMultipleAssigneesEnabled,
    (openTasks, openSubtasks, activeProjectIds, teamUsers, allUsers, isMultipleAssigneesEnabled) => {
      const teamUserIds = teamUsers.map((u) => u.id);
      const result = {};
      const today = moment.utc(moment().format("YYYY-MM-DD"));

      const resolveDueOn = (dueOn) => {
        const dueOnObj = moment.unix(dueOn).utc();
        if (!dueOn) {
          return "notSet";
        } else {
          if (today.isAfter(dueOnObj)) {
            return "overdue";
          } else if (today.isSame(dueOnObj)) {
            return "today";
          } else if (dueOnObj.diff(today, "days") === 1) {
            return "tomorrow";
          } else if (today.isSame(dueOnObj, "week")) {
            return "restOfWeek";
          } else if (dueOnObj.isSame(today.clone().add(1, "week"), "week")) {
            return "nextWeek";
          } else {
            return "upcoming";
          }
        }
      };

      openTasks.forEach((task) => {
        const taskId = task.get("id");
        const dueOnGroup = resolveDueOn(task.get("due_on"));

        if (!activeProjectIds.includes(task.get("project_id"))) return;

        // When multi-assignee is enabled, fan out the task per each team member in assignee_ids.
        // Fall back to assignee_id if assignee_ids is absent (either/or, not both).
        const assigneeIds = isMultipleAssigneesEnabled ? task.get("assignee_ids") : null;
        const effectiveAssignees = isMultipleAssigneesEnabled
          ? assigneeIds && assigneeIds.size > 0
            ? assigneeIds.filter((id) => teamUserIds.includes(id))
            : []
          : teamUserIds.includes(task.get("assignee_id"))
          ? [task.get("assignee_id")]
          : [];

        effectiveAssignees.forEach((assigneeId) => {
          set(result, [assigneeId, dueOnGroup, `Task-${taskId}`], {
            id: taskId,
            type: "task",
            name: task.get("name"),
            dueOn: task.get("due_on"),
            startOn: task.get("start_on"),
            assigneeId: +assigneeId,
            dueOnId: `${assigneeId}-${dueOnGroup}`,
          });
        });
      });

      openSubtasks.forEach((subtask) => {
        if (teamUserIds.includes(subtask.assignee_id)) {
          const taskId = subtask.task_id;
          const task = openTasks.get(`${taskId}`);
          const dueOn = subtask.due_on ? subtask.due_on : task ? task.get("due_on") : null;
          const startOn = subtask.due_on ? subtask.due_on : task ? task.get("start_on") : null;

          if (task && activeProjectIds.includes(task.get("project_id"))) {
            const dueOnGroup = resolveDueOn(dueOn);
            const subtaskAttrs = {
              id: subtask.id,
              type: "subtask",
              name: subtask.name,
              dueOn: dueOn,
              startOn: startOn,
              assigneeId: +subtask.assignee_id,
              dueOnId: `${subtask.assignee_id}-${dueOnGroup}`,
            };
            set(result, [subtask.assignee_id, dueOnGroup, `Subtask-${subtask.id}`], subtaskAttrs);
          }
        }
      });

      const flatResult = [];

      const dueOnSortOrder = {
        overdue: 1,
        started: 2,
        today: 3,
        tomorrow: 4,
        restOfWeek: 5,
        nextWeek: 6,
        upcoming: 7,
        notSet: 8,
      };

      Object.keys(result)
        .sort((a, b) => {
          const aName = allUsers.get(`${a}`).getName();
          const bName = allUsers.get(`${b}`).getName();
          return sortCompare(aName.toLowerCase(), bName.toLowerCase());
        })
        .forEach((assigneeId) => {
          flatResult.push({
            id: +assigneeId,
            name: allUsers.get(`${assigneeId}`).getName(),
            type: "assignee",
          });
          Object.keys(result[assigneeId])
            .sort((a, b) => {
              return dueOnSortOrder[a] - dueOnSortOrder[b];
            })
            .forEach((dueOnIndicator) => {
              flatResult.push({
                id: `${assigneeId}-${dueOnIndicator}`,
                name: dueOnIndicator,
                type: "dueOnIndicator",
                assigneeId: +assigneeId,
                count: Object.keys(result[assigneeId][dueOnIndicator])?.length,
              });
              Object.values(result[assigneeId][dueOnIndicator])
                .sort((a, b) => {
                  if (a.dueOn === b.dueOn) {
                    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
                  }
                  return a.dueOn < b.dueOn ? -1 : 1;
                })
                .forEach((item) => {
                  flatResult.push(item);
                });
            });
        });

      return flatResult;
    }
  );
};

export const userWork = createSelector(
  getSampleProjectIds,
  getAllTasks,
  getSubtasksArray,
  getLoggedUserId,
  (state, userIds) => userIds,
  getIsMultipleAssigneesEnabled,
  (sampleProjectIds, tasks, subtasks, loggedUserId, userIds, isMultipleAssigneesEnabled) => {
    const resolvedUserIds = !userIds ? [loggedUserId] : Array.isArray(userIds) ? userIds : [userIds];

    const result = {
      projects: [],
      assignees: [],
      taskListByProjectMap: {},
      tasksByTaskListMap: {},
      subtaskTasksByTaskListMap: {},
      subtasksByTaskMap: {},
    };
    const assignedSubtasks = subtasks.filter((subtask) => {
      return subtask && !subtask.is_trashed && !subtask.is_completed && resolvedUserIds.includes(subtask.assignee_id);
    });

    const taskIds = assignedSubtasks.reduce((accum, subtask) => {
      const taskId = subtask.task_id;

      if (accum.indexOf(taskId) < 0) {
        accum.push(taskId);
      }

      return accum;
    }, []);

    tasks.forEach((task) => {
      const projectId = task.get("project_id");
      const isTaskFromSampleProject = sampleProjectIds.indexOf(projectId) > -1;
      const taskListId = task.get("task_list_id");
      const taskId = task.get("id");
      const hasAssignedSubtasks = taskIds.some((id) => id === taskId);

      // Resolve which of resolvedUserIds are assigned to this task.
      // When multi-assignee is enabled, a task may have assignee_ids OR assignee_id (not necessarily both).
      let assignedUserIds;
      if (isMultipleAssigneesEnabled) {
        const assigneeIds = task.get("assignee_ids");
        if (assigneeIds && assigneeIds.size > 0) {
          assignedUserIds = assigneeIds.filter((id) => resolvedUserIds.includes(id)).toArray();
        } else {
          const id = task.get("assignee_id");
          assignedUserIds = resolvedUserIds.includes(id) ? [id] : [];
        }
      } else {
        const id = task.get("assignee_id");
        assignedUserIds = resolvedUserIds.includes(id) ? [id] : [];
      }

      const assigned = assignedUserIds.length > 0;

      if (!task.get("is_trashed") && !task.get("is_completed") && !isTaskFromSampleProject) {
        if ((hasAssignedSubtasks || assigned) && result.projects.indexOf(projectId) < 0) {
          result.projects.push(projectId);
        }

        if (assigned) {
          assignedUserIds.forEach((id) => {
            if (result.assignees.indexOf(id) < 0) {
              result.assignees.push(id);
            }
          });
        }

        if (hasAssignedSubtasks || assigned) {
          if (!result.taskListByProjectMap[projectId]) {
            result.taskListByProjectMap[projectId] = [taskListId];
          } else if (result.taskListByProjectMap[projectId].indexOf(taskListId) < 0) {
            result.taskListByProjectMap[projectId].push(taskListId);
          }
        }

        if (assigned) {
          if (!result.tasksByTaskListMap[taskListId]) {
            result.tasksByTaskListMap[taskListId] = [taskId];
          } else if (result.tasksByTaskListMap[taskListId].indexOf(taskId) < 0) {
            result.tasksByTaskListMap[taskListId].push(taskId);
          }
        }

        if (hasAssignedSubtasks && !assigned) {
          if (!result.subtaskTasksByTaskListMap[taskListId]) {
            result.subtaskTasksByTaskListMap[taskListId] = [taskId];
          } else if (result.subtaskTasksByTaskListMap[taskListId].indexOf(taskId) < 0) {
            result.subtaskTasksByTaskListMap[taskListId].push(taskId);
          }
        }
      }
    });

    assignedSubtasks.forEach((subtask) => {
      const subtaskId = subtask.id;
      const taskId = subtask.task_id;
      const assigneeId = subtask.assignee_id;
      const task = tasks.find((t) => t.get("id") === taskId);
      // make sure that subtask is not from sample project
      if (task && sampleProjectIds.indexOf(task.get("project_id")) === -1) {
        if (result.assignees.indexOf(assigneeId) < 0) {
          result.assignees.push(assigneeId);
        }

        if (!result.subtasksByTaskMap[taskId]) {
          result.subtasksByTaskMap[taskId] = [];
        }

        if (result.subtasksByTaskMap[taskId].indexOf(subtaskId) < 0) {
          result.subtasksByTaskMap[taskId].push(subtaskId);
        }
      }
    });

    return result;
  }
);

export const getUserWorkProjects = createSelector(
  userWork,
  getAllProjects,
  (state) => getPersonalizedConfigOption(state, "my_work_projects_order"),
  (state, userId) => userId,
  ({ projects }, describedProjects, myWorkProjectsOrder, userId) => {
    const result = projects
      .reduce((accumulator, projectId) => {
        const project = describedProjects.get(`${projectId}`);
        if (project && !project.get("is_sample") && !project.get("is_completed") && !project.get("is_trashed")) {
          accumulator.push({
            id: project.get("id"),
            name: project.get("name"),
            companyId: project.get("company_id"),
          });
        }
        return accumulator;
      }, [])
      .sort((a, b) => sortCompare(a.name.toLowerCase(), b.name.toLowerCase()));

    if (!userId) {
      const map = myWorkProjectsOrder?.reduce((acc, value, index) => ((acc[value] = index), acc), {}) || {};
      const nextIndex = myWorkProjectsOrder?.size || 0;

      return result.sort((a, b) => {
        const firstIndex = map[a.id] ?? nextIndex;
        const secondIndex = map[b.id] ?? nextIndex;
        return sortCompare(firstIndex, secondIndex);
      });
    }

    return result;
  }
);

export const makeGetProjectsForVirtualizedList = (membersIds) => {
  return createSelector(
    (state) => getUserWorkProjects(state, membersIds),
    getAllTaskLists,
    getOpenTasks,
    getOpenSubtasks,
    getLoggedUserId,
    getIsMultipleAssigneesEnabled,
    (workProjects, taskLists, openTasks, openSubtasks, loggedUserId, isMultipleAssigneesEnabled) => {
      const resolvedMemberIds = membersIds ?? [loggedUserId];
      const subtaskWithTaskList = openSubtasks.map((subtask) => ({
        ...subtask,
        task_list_id: openTasks?.get(`${subtask.task_id}`)?.get("task_list_id"),
      }));

      const flatData = [];

      const projects = workProjects?.map((p) => {
        const lists = Object.values(taskLists?.filter((list) => list.get(`project_id`) === p.id).toJS());
        return {
          ...p,
          lists: lists
            .map((list) => {
              const tasks = openTasks
                .filter((task) => {
                  if (task.get(`project_id`) !== p.id || task.get(`task_list_id`) !== list.id) return false;
                  if (isMultipleAssigneesEnabled) {
                    const assigneeIds = task.get(`assignee_ids`);

                    return assigneeIds && assigneeIds.some((id) => resolvedMemberIds.includes(id));
                  }
                  return resolvedMemberIds.includes(task.get(`assignee_id`));
                })
                .toJS();
              return {
                ...list,
                items: [
                  ...Object.values(tasks),
                  ...subtaskWithTaskList
                    .filter(
                      (subtask) => resolvedMemberIds.includes(subtask.assignee_id) && subtask.task_list_id === list.id
                    )
                    .map((subtask) => ({ ...subtask, name: subtask.body })),
                ].sort((a, b) => {
                  if (a.position === b.position && a.class === "Task") {
                    if (a.name === b.name) {
                      return descSortCompare(a.created_on, b.created_on);
                    }
                    return a.name.localeCompare(b.name);
                  }
                  return sortCompare(a.position, b.position);
                }),
              };
            })
            .filter((l) => l.items.length)
            .sort((a, b) => sortCompare(a.position, b.position)),
        };
      });

      projects.forEach((project) => {
        const { lists, ...filteredProject } = project;
        flatData.push({
          type: "project",
          project: filteredProject,
        });

        lists.forEach((list) => {
          flatData.push({
            type: "list",
            list: { id: list.id, name: list.name, project_id: list.project_id },
          });

          list?.items?.forEach((item) => {
            if (item.class === "Task")
              return flatData.push({
                type: "task",
                task: { id: item.id, project_id: item.project_id, task_list_id: item.task_list_id },
              });
          });

          list?.items?.forEach((item) => {
            if (item.class === "Subtask")
              return flatData.push({
                type: "subtask",
                subtask: { id: item.id, project_id: item.project_id, task_list_id: item.task_list_id },
              });
          });
        });
      });

      return flatData;
    }
  );
};

export const makeGetUserTaskListsSelector = (projectId, userId) => {
  return createSelector(
    (state) => userWork(state, userId),
    (state) => state.task_lists,
    (collection, allTaskLists) => {
      const taskLists = collection.taskListByProjectMap[projectId];

      if (taskLists) {
        return taskLists
          .reduce((acc, taskListId) => {
            const taskList = allTaskLists.get(`${taskListId}`);
            if (taskList && !taskList.get("is_trashed") && !taskList.get("is_completed")) {
              acc.push({
                id: taskListId,
                name: taskList.get("name"),
                position: taskList.get("position"),
              });
            }
            return acc;
          }, [])
          .sort((a, b) => sortCompare(a.position, b.position));
      }

      return [];
    }
  );
};

export const makeGetUserTasksSelector = (taskListId, userId) => {
  return createSelector(
    (state) => userWork(state, userId),
    getAllTasks,
    (collection, allTasks) => {
      const tasksByTaskList = collection.tasksByTaskListMap[taskListId];
      if (tasksByTaskList) {
        const tasks = tasksByTaskList.reduce((acc, taskId) => {
          const task = allTasks.get(`${taskId}`);
          if (task) {
            acc.push({
              id: taskId,
              name: task.get("name"),
              position: task.get("position"),
            });
          }
          return acc;
        }, []);

        return tasks.sort((a, b) => sortCompare(a.position, b.position));
      }
      return [];
    }
  );
};

export const makeGetUserSubtasksSelector = (taskListId, userId) => {
  return createSelector(
    (state) => userWork(state, userId),
    getAllTasks,
    ({ subtasksByTaskMap, tasksByTaskListMap, subtaskTasksByTaskListMap }, tasks) => {
      const assignedTasks = tasksByTaskListMap[taskListId] ? tasksByTaskListMap[taskListId] : [];
      const subtaskTasks = subtaskTasksByTaskListMap[taskListId] ? subtaskTasksByTaskListMap[taskListId] : [];
      let collection = [...assignedTasks, ...subtaskTasks];

      collection = collection.sort((a, b) => {
        const first = tasks.get(`${a}`);
        const second = tasks.get(`${b}`);
        if (first && second) {
          return first.position < second.position ? -1 : 1;
        }
        return 0;
      });
      return collection.reduce((acc, taskId) => {
        const subtaskIds = subtasksByTaskMap[taskId];
        if (subtaskIds) {
          subtaskIds.forEach((subtaskId) => {
            if (acc.indexOf(subtaskId) < 0) {
              acc.push(subtaskId);
            }
          });
        }
        return acc;
      }, []);
    }
  );
};

export const makeGetUserAssignmentsCountSelector = (projectId, userId) => {
  return createSelector(
    (state) => userWork(state, userId),
    ({ taskListByProjectMap, tasksByTaskListMap, subtaskTasksByTaskListMap, subtasksByTaskMap }) => {
      let count = 0;

      let taskLists = [];

      if (projectId) {
        taskLists = taskListByProjectMap[projectId];
      } else {
        Object.values(taskListByProjectMap).map((taskListArray) => {
          taskLists = [...taskLists, ...taskListArray];
          return taskLists;
        });
      }

      if (taskLists) {
        taskLists.forEach((taskListId) => {
          const assignedTasks = tasksByTaskListMap[taskListId] ? tasksByTaskListMap[taskListId] : [];
          const subtaskTasks = subtaskTasksByTaskListMap[taskListId] ? subtaskTasksByTaskListMap[taskListId] : [];
          const collection = [...assignedTasks, ...subtaskTasks];
          count += assignedTasks.length;
          collection.forEach((taskId) => {
            const assignedSubtasks = subtasksByTaskMap[taskId] ? subtasksByTaskMap[taskId] : [];
            count += assignedSubtasks.length;
          });
        });
      }

      return count;
    }
  );
};

export const getUserTasksCount = createSelector(
  (state, userId) => userWork(state, userId),
  ({ tasksByTaskListMap, subtasksByTaskMap }) => {
    const reducer = (accumulator, currentValue) => accumulator + currentValue.length;
    const tasksCount = Object.values(tasksByTaskListMap).reduce(reducer, 0);
    const subtasksCount = Object.values(subtasksByTaskMap).reduce(reducer, 0);
    return {
      tasksCount,
      subtasksCount,
    };
  }
);

export const getUserHaveAssignments = createSelector(
  getAllProjects,
  getAllTasks,
  getSubtasksArray,
  getLoggedUserId,
  (state, userId) => userId,
  getIsMultipleAssigneesEnabled,
  (projects, tasks, subtasks, loggedUserId, id, isMultipleAssigneesEnabled) => {
    const userIds = !id ? [loggedUserId] : Array.isArray(id) ? id : [id];
    const activeProjects = projects.filter((project) => {
      return !project.get("is_trashed") && !project.get("is_completed") && !project.get("is_sample");
    });
    const activeTasks = tasks.filter((task) => {
      return !task.get("is_completed") && !task.get("is_trashed");
    });
    if (
      subtasks.some((subtask) => {
        const task = activeTasks.get(`${subtask?.task_id}`);
        if (!task) {
          return false;
        }
        const project = activeProjects.get(`${task.get("project_id")}`);
        return project && !subtask.is_trashed && !subtask.is_completed && userIds.includes(subtask.assignee_id);
      })
    ) {
      return true;
    } else if (
      activeTasks.some((task) => {
        const project = activeProjects.get(`${task.get("project_id")}`);
        if (isMultipleAssigneesEnabled) {
          const assigneeIds = task.get("assignee_ids");
          if (!assigneeIds || assigneeIds.size === 0) return false;
          return project && userIds.some((id) => assigneeIds?.includes(id));
        }
        return project && userIds.includes(task.get("assignee_id"));
      })
    ) {
      return true;
    }
    return false;
  }
);

export const getUserTaskIdsSortByDate = createSelector(
  (state, groupBy, userId) => userWorkGroupByDate(state, groupBy, userId),
  (collection) => {
    const taskIds = Object.keys(collection)
      .map((key) => {
        return collection[key];
      })
      .flat()
      .map((item) => item.taskId);

    return [...new Set(taskIds)];
  }
);

export const getTeamWorkAssignees = createSelector(
  (state, membersIds) => userWork(state, membersIds),
  getAllUsers,
  ({ assignees }, users) => {
    return assignees
      .reduce((accumulator, assigneeId) => {
        const user = users.get(`${assigneeId}`);
        if (user) {
          accumulator.push({
            id: user.get("id"),
            name: user.getName(),
            avatarUrl: user.getAvatarUrl(),
          });
        }
        return accumulator;
      }, [])
      .sort((a, b) => sortCompare(a.name.toLowerCase(), b.name.toLowerCase()));
  }
);
